import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { navigateToUrl } from "single-spa";

/* ---------------------------------------------------------------------------
 * UI Components
 * ---------------------------------------------------------------------------
 */
import { TheHeader, DrawerHost, UpdatePage } from "../components";
import {
  TheUnifiedNavigation,
  AlertModal,
  AppSnackbar,
  GenericModalState,
} from "@maropost-ui/liquidsky-ui";
import OnboardingMenu from "../components/onboarding/NavigationMenu.vue";

/* ---------------------------------------------------------------------------
 * Stores & Services
 * ---------------------------------------------------------------------------
 */
import { useUserStore, useShellBridgeStore } from "../stores";
import { useNavigation } from "../composable/sidemenu/unifiedNavigation";
import { alertModalService } from "@/lib/services/alertModalService";
import { onBoardingService } from "@/lib/services/onBoardingService";
import { shouldFetchOnboardingData } from "@/lib/utils/onboardingAccess";
import { useHubSpotChat } from "@/lib/plugins/hubspotChat";
import { getPurchaseChangeType } from "@/lib/utils/packages";
import { getPreviousProductPlans } from "@/lib/utils/previousProductPlans";
import {
  trackAmplitudeEvent,
  triggerSnackbar,
} from "@/lib/utils/utilityFunctions";

import { useGlobalTrialState } from "../composable/freeTrail/useGlobalTrialState";
import { usePackageFeaturesPolling } from "../composable/packages/usePackageFeaturesPolling";

/* ============================================================================
 * STORES & ROUTER
 * ============================================================================
 */

// User / permissions store
const PACKAGE_UPDATE_GRACE_PERIOD_MS = 10 * 60 * 1000; // 10 minutes


export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props, { expose: __expose }) {
  __expose();

/**
 * ============================================================================
 * Unified Shell – Navigation & Loader Controller
 * ============================================================================
 *
 * Behavior Summary
 * ----------------
 * - Displays unified navigation ONLY for supported product routes
 *   (Marketing, Service, Commerce).
 * - Navigation menu is loaded lazily and reacts to route changes.
 * - Global loader visibility is controlled via cross-MFE events.
 *
 * Communication Model
 * -------------------
 * - MFEs dispatch `MFE_LOADED` events to signal loading state.
 * - Shell listens and updates `loading` accordingly.
 * - MFEs can also request navigation rail changes via custom events.
 *
 * Design Notes
 * ------------
 * - Shell remains framework-agnostic.
 * - MFEs do not directly import shell dependencies.
 * - All UI side-effects are centralized here.
 * ============================================================================
 */

const userStore = useUserStore();

// Vue Router instance
const router = useRouter();
const route = useRoute();

// Global alert modal reactive state
const alertModalState = alertModalService.getState();

// Global loader state (controlled by MFEs)
const loading = ref(false);

// Show banner state
const showBanner = computed(() => userStore.getCTABanner);
const hubSpotChat = useHubSpotChat();
const shellBridgeStore = useShellBridgeStore();

const snackbarMessage = computed(
  () => shellBridgeStore.getSnackbarMessage
)

const showSnackbar = computed({
  get: () => Object.entries(snackbarMessage.value).length > 0 ,
  set: (value) => {
    if (!value) {
      shellBridgeStore.setSnackbarMessage({});
    }
  },
})
/**
 * Load HubSpot chat after the shell loader is idle (initial mount or MFE finished).
 */
const loadHubSpotWhenIdle = (): void => {
  if (!loading.value) {
    hubSpotChat.loadIfEnabled();
  }
};

onMounted(loadHubSpotWhenIdle);

watch(loading, loadHubSpotWhenIdle, { flush: "post" });

// Sales navigation visibility (permission-based)
const saleNavigation = computed(() => userStore.getSaleNavigation);

// Normalized alert modal props
const alertModalItems = computed<GenericModalState>(() => ({
  title: alertModalState.title,
  subtitle: alertModalState.subtitle,
  description: alertModalState.description,
  // Backdrop is off only for the standard feature-limit popup; see alertModalService.showAlert()
  options: { scrim: alertModalState.showBackdrop },
}));

  /**
   * Get the trial data
   */
  const trialData:any = computed(() => userStore.getTrailPackageFeatures);

  /**
   * Use the trial countdown composable
   */

  const { trialDays } = useGlobalTrialState(
    trialData,
    userStore
  );

  /**
   * Hide the banner
   */
  const hideBanner = () => {
    userStore.setCTABanner(false);
  }

  /**
   * Navigate to the billing page
   */
  const navigateToCheckout = () => {
    let accountId = userStore.getAccountId
    navigateToUrl(`/accounts/${accountId}/checkout`)  }

  /**
   * Check if the trial is expired
   */
   const isTrialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired);


   /**
    * 
    */
   const isNetoMhcAccountTraialExpired = computed(() => userStore.getIsTrailAccount && userStore.getIsTrialExpired && !userStore.getProductAccess('neto') && !userStore.getProductAccess('mhc'));

/**
 * Left navigation visibility.
 * Hidden on routes that opt out via meta (e.g. account setup) or when
 * a Neto/MHC trial account has expired.
 */
const showNavigation = computed(() => {
  const navigationEnabled = (route.meta.navigation as { show?: boolean } | undefined)?.show !== false
  return navigationEnabled && !isNetoMhcAccountTraialExpired.value
});

/* ============================================================================
 * NAVIGATION STATE
 * ============================================================================
 */

/**
 * Unified navigation configuration
 * - Generated dynamically based on user permissions
 * - Passed directly to <TheUnifiedNavigation />
 */
const { navigation } = useNavigation();

/**
 * Shows a snackbar when a purchase is successful
 */
function showPurchaseSuccessSnackbar(): void {
  const previousProductPlans = getPreviousProductPlans(userStore.getAccountId);
  if (!previousProductPlans || userStore.isLegacyAccount) return;

  const hasCoreProducts =
    userStore.getProductAccess("marketing") ||
    userStore.getProductAccess("service") ||
    userStore.getProductAccess("commerce");

  if (!hasCoreProducts) return;

  let message = "";

  for (const productKey of ["mmc", "mcc", "msc"] as const) {
    const changeType = getPurchaseChangeType(
      previousProductPlans[productKey],
      userStore.getCurrentPackagePlan(productKey),
      productKey
    );

    if (changeType === "upgrade") {
      message =
        "Your payment was successful and your plan has been updated.";
      trackAmplitudeEvent("Checkout Completed", { isFirstPurchase: false });
      break;
    }

    if (changeType === "first_purchase") {
      message =
        "Your payment was successful and your subscription is now active.";
      trackAmplitudeEvent("Checkout Completed", { isFirstPurchase: true });
    }
  }

  if (message) {
    triggerSnackbar({ message, type: "success" });
  }
}

onMounted(() => {
  if (shouldFetchOnboardingData(userStore)) {
    onBoardingService.getOnboardingData().catch((err) => {
      console.error("Failed to fetch onboarding data:", err);
    });
  }

  showPurchaseSuccessSnackbar();
});

/**
 * Poll package features hourly so an expiry mid-session opens the modal above.
 */
const { hasPackageUpdate } = usePackageFeaturesPolling();

/* ============================================================================
 * ALERT MODAL: DISMISS ON PAGE CHANGE
 * ============================================================================
 * The alert has no backdrop, so users can navigate away while it is open.
 * Hide it when the pathname changes so it only stays on the page that raised it.
 * "before-routing-event" fires before the next page mounts, so an alert raised
 * by the destination page is not affected.
 */
let lastPathname = window.location.pathname;

const hideAlertOnPageChange = () => {
  const { pathname } = window.location;
  if (pathname === lastPathname) return;
  lastPathname = pathname;
  alertModalService.hide();
};

onMounted(() => {
  window.addEventListener("single-spa:before-routing-event", hideAlertOnPageChange);
});

onBeforeUnmount(() => {
  window.removeEventListener("single-spa:before-routing-event", hideAlertOnPageChange);
});

/* ============================================================================
 * CROSS-MFE EVENT HANDLERS
 * ============================================================================
 */

/**
 * Handle MFE loader lifecycle events
 *
 * Event: MFE_LOADED
 * Payload:
 *   - true  → show loader
 *   - false → hide loader
 *
 * Dispatched by:
 *   - Shell (before mounting/unmounting MFEs)
 *   - Individual MFEs (when fully initialized)
 */
const onMfeLoaded = (event: Event) => {
  const customEvent = event as CustomEvent<boolean>;
  loading.value = customEvent.detail;
};

// Register loader event listener
window.addEventListener("MFE_LOADED", onMfeLoaded);

/**
 * Handle navigation rail toggle requests from MFEs
 *
 * Event: mfe:folder-open
 * Payload:
 *   - true  → open navigation rail
 *   - false → close navigation rail
 *
 * Used when MFEs want to temporarily control layout
 * (e.g. opening a folder or deep view)
 */
window.addEventListener("mfe:folder-open", (event) => {
  const customEvent = event as CustomEvent<boolean>;
  if (customEvent.detail !== undefined) {
    userStore.setNavigationRail(customEvent.detail);
  }
});

/* ============================================================================
 * UI ACTIONS
 * ============================================================================
 */

/**
 * Toggle left navigation drawer manually
 * (Triggered by header / hamburger icon)
 */
const toggleLeftMenu = (isDrawerClosed: boolean): void => {
  userStore.setNavigationRail(isDrawerClosed);
};

/**
 * Handle alert modal confirmation
 *
 * Scenario:
 * - User hits usage limit for a feature
 * - Redirect them to Account & Billing page
 *
 * Routing Logic:
 * - Marketing users → SPA navigation
 * - Non-marketing users → full page redirect
 */
const handleAlertConfirm = () => {
  const accountId = userStore.getAccountId;
  const path = `/accounts/${accountId}/${userStore.isLegacyAccount ? 'account-&-billing/subscriptions' : 'checkout'}`;

  if (
    !userStore.getProductAccess("marketing") ||
    !userStore.getProductAccessOnRoles("marketing")
  ) {
    router.push(path);
  } else {
    navigateToUrl(path);
  }

  alertModalService.hide();
};

/**
 *
 * Closing starts a short grace period so the user can save any in-progress
 */
const handlePackageUpdateClose = () => {
  hasPackageUpdate.value = false;
  setTimeout(() => {
    const accountId = userStore.getAccountId;
    userStore.setAccountId(0);
    window.location.href = `/accounts/${accountId}/dashboard`;
  }, PACKAGE_UPDATE_GRACE_PERIOD_MS);
};

const __returned__ = { userStore, router, route, alertModalState, loading, showBanner, hubSpotChat, shellBridgeStore, snackbarMessage, showSnackbar, loadHubSpotWhenIdle, saleNavigation, alertModalItems, trialData, trialDays, hideBanner, navigateToCheckout, isTrialExpired, isNetoMhcAccountTraialExpired, showNavigation, navigation, showPurchaseSuccessSnackbar, hasPackageUpdate, get lastPathname() { return lastPathname }, set lastPathname(v) { lastPathname = v }, hideAlertOnPageChange, onMfeLoaded, toggleLeftMenu, handleAlertConfirm, PACKAGE_UPDATE_GRACE_PERIOD_MS, handlePackageUpdateClose, get TheHeader() { return TheHeader }, get DrawerHost() { return DrawerHost }, get UpdatePage() { return UpdatePage }, get TheUnifiedNavigation() { return TheUnifiedNavigation }, get AlertModal() { return AlertModal }, get AppSnackbar() { return AppSnackbar }, OnboardingMenu, get alertModalService() { return alertModalService } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})